import type { MenuDefinition, MenuGroup } from './types';

/**
 * Default clinic-portal menu (Medix baseline).
 *
 * Used by every site type that does not ship its own menu in `customize/`.
 * Entries only some clinic families may see carry `families` instead of being
 * filtered out by label somewhere else -- visibility is declared where the
 * entry is declared.
 */

const MEDIX_ONLY = ['medix'];

/** A2 (鍼灸) - Acupuncture Service */
const menuGroupsA2: MenuGroup[] = [
  {
    id: 'daily',
    label: '日次業務',
    icon: <i className="fa fa-clipboard" aria-hidden="true" />,
    items: [
      { label: '保険証・同意書入力', path: '/patients/edit' },
      { label: '施術入力', path: '/visit_clinic/schedule' },
      { label: '訪問入力', path: '/visits/schedule' },
      { label: '保険証読取', path: '/patients/certification' },
      { label: '継続受診者登録', path: '/patients/continuation' },
      { label: '[来院用]領収書(窓口)', path: '/rezepts/visit_clinic_invoices_init' },
      { label: '[訪問用]領収書・請求書', path: '/rezepts/invoices_init' },
    ],
  },
  {
    id: 'monthly',
    label: '月次業務',
    icon: <i className="fa fa-calendar-o" aria-hidden="true" />,
    items: [
      { label: 'レセプト発行', path: '/rezepts/init' },
      { label: '施術録出力', path: '/treatment_records/init' },
      { label: '返戻処理', path: '/rezepts/return_init' },
      { label: '通知センター', path: '/notification_center' },
      { label: '加療期間期限切れリスト', path: '/consents/treatment_period' },
      { label: '統計一覧表', path: '/rezepts/statistical_init' },
      { label: '患者情報検索', path: '/patients/search_patients' },
      { label: 'レセプト内容CSV出力', path: '/rezept_histories/init' },
      { label: '訪問予定表(患者毎)', path: '/visits/patient_init' },
      { label: '訪問予定表（施術師毎）', path: '/visits/massager_init' },
      { label: '患者リスト(施術師毎)', path: '/visits/visitpatient_init' },
      { label: '施術師毎CSV出力', path: '/rezepts/init_massagers' },
    ],
  },
  {
    id: 'master',
    label: 'マスタメンテナンス',
    icon: <i className="fa fa-cog" aria-hidden="true" />,
    items: [
      { label: '治療院基本情報', path: '/clinic_informations/init' },
      { label: '県別注意事項', path: '/insurer_attentions/init', families: MEDIX_ONLY },
      { label: 'はり・きゅう師', path: '/massagers/init' },
      { label: '医療機関', path: '/hospitals/init' },
      { label: '自由診療メニュー', path: '/free_medical/init' },
      { label: '健康保険料金', path: '/medical_expense_prices/init' },
      { label: '文章テンプレート', path: '/document_templates/init' },
      { label: '拠点管理', path: '/clinic_locations/init' },
    ],
  },
  {
    id: 'sheets',
    label: 'レセプト用紙発注',
    icon: <i className="fa fa-shopping-cart" aria-hidden="true" />,
    families: MEDIX_ONLY,
    items: [
      { label: '発注履歴', path: '/sheets/histories' },
      { label: '印刷用紙発注', path: '/sheets/order' },
    ],
  },
  {
    id: 'support',
    label: 'サポート',
    icon: <i className="fa fa-comments-o" aria-hidden="true" />,
    items: [
      { label: 'お問い合わせ', path: '/contact/edit' },
      { label: '遠隔サポート', path: 'https://113366.com/rece', openInNewTab: true },
    ],
  },
  {
    id: 'manual',
    label: 'マニュアル',
    icon: <i className="fa fa-book" aria-hidden="true" />,
    items: [
      {
        label: 'A2マニュアル',
        path: '/manual/a2',
        externalUrl: '/medix/manual/r2_manual/★A2マニュアル統合版（20230529）.pdf',
      },
      {
        label: 'チェックイン機能マニュアル',
        path: '/manual/checkin',
        externalUrl: '/medix/manual/r2_manual/A2マニュアル_42_チェックイン機能.pdf',
      },
    ],
  },
  {
    id: 'medix',
    label: 'メディックス',
    icon: <i className="fa fa-heart" aria-hidden="true" />,
    families: MEDIX_ONLY,
    items: [
      {
        label: '会員専用ページ',
        path: 'https://member.mdx-center.com/login/index',
        openInNewTab: true,
      },
      {
        label: '計算書開示システム',
        path: 'https://www.mdx-calculate.com/AccountWebSystem_APM/View/V_login.jsp',
        openInNewTab: true,
      },
    ],
  },
  {
    id: 'design-template',
    label: 'Design Template',
    icon: <i className="fa fa-palette" aria-hidden="true" />,
    path: '/design-template',
  },
];

/** R2 (マッサージ) - Massage Service */
const menuGroupsR2: MenuGroup[] = [
  {
    id: 'daily',
    label: '日次業務',
    icon: <i className="fa fa-clipboard" aria-hidden="true" />,
    items: [
      { label: '保険証・同意書入力', path: '/patients/edit' },
      { label: '施術入力', path: '/visit_clinic/schedule' },
      { label: '訪問入力', path: '/visits/schedule' },
      { label: '保険証読取', path: '/patients/certification' },
      { label: '[来院用]領収書(窓口)', path: '/rezepts/visit_clinic_invoices_init' },
      { label: '[訪問用]領収書・請求書', path: '/rezepts/invoices_init' },
    ],
  },
  {
    id: 'monthly',
    label: '月次業務',
    icon: <i className="fa fa-calendar-o" aria-hidden="true" />,
    items: [
      { label: 'レセプト発行', path: '/rezepts/init' },
      { label: '施術録出力', path: '/treatment_records/init' },
      { label: '返戻処理', path: '/rezepts/return_init' },
      { label: '通知センター', path: '/notification_center' },
      { label: '加療期間期限切れリスト', path: '/consents/treatment_period' },
      { label: '統計一覧表', path: '/rezepts/statistical_init' },
      { label: '患者情報検索', path: '/patients/search_patients' },
      { label: 'レセプト内容CSV出力', path: '/rezept_histories/init' },
      { label: '予定表(患者毎)', path: '/visits/patient_init' },
      { label: '訪問予定表（マッサージ師毎）', path: '/visits/massager_init' },
      { label: '患者リスト(マッサージ師毎)', path: '/visits/visitpatient_init' },
      { label: '施術師毎CSV出力', path: '/rezepts/init_massagers' },
    ],
  },
  {
    id: 'master',
    label: 'マスタメンテナンス',
    icon: <i className="fa fa-cog" aria-hidden="true" />,
    items: [
      { label: '治療院基本情報', path: '/clinic_informations/init' },
      { label: '県別注意事項', path: '/insurer_attentions/init', families: MEDIX_ONLY },
      { label: 'マッサージ師', path: '/massagers/init' },
      { label: '医療機関', path: '/hospitals/init' },
      { label: '自由診療メニュー', path: '/free_medical/init' },
      { label: '健康保険料金', path: '/medical_expense_prices/init' },
      { label: '文章テンプレート', path: '/document_templates/init' },
      { label: '拠点管理', path: '/clinic_locations/init' },
    ],
  },
  {
    id: 'sheets',
    label: 'レセプト用紙発注',
    icon: <i className="fa fa-shopping-cart" aria-hidden="true" />,
    families: MEDIX_ONLY,
    items: [
      { label: '印刷用紙発注', path: '/sheets/order' },
      { label: '発注履歴', path: '/sheets/histories' },
    ],
  },
  {
    id: 'support',
    label: 'サポート',
    icon: <i className="fa fa-comments-o" aria-hidden="true" />,
    items: [
      { label: 'お問い合わせ', path: '/contact/edit' },
      { label: '遠隔サポート', path: 'https://113366.com/rece', openInNewTab: true },
    ],
  },
  {
    id: 'manual',
    label: 'マニュアル',
    icon: <i className="fa fa-book" aria-hidden="true" />,
    items: [
      {
        label: 'R2マニュアル',
        path: '/manual/r2',
        externalUrl: '/medix/manual/r2_manual/★R2マニュアル統合版（20220606）.pdf',
      },
      {
        label: 'チェックイン機能マニュアル',
        path: '/manual/checkin',
        externalUrl: '/medix/manual/r2_manual/R2マニュアル_34_チェックイン機能.pdf',
      },
    ],
  },
  {
    id: 'design-template',
    label: 'Design Template',
    icon: <i className="fa fa-palette" aria-hidden="true" />,
    path: '/design-template',
  },
];

/** Classic sidebar has no 一般 / 管理 split -- one implicit tab. */
export const medixMenuA2: MenuDefinition = {
  tabs: [{ id: 'main', label: 'メニュー', groups: menuGroupsA2 }],
};

export const medixMenuR2: MenuDefinition = {
  tabs: [{ id: 'main', label: 'メニュー', groups: menuGroupsR2 }],
};
